import type { WorkflowStatisticsResult } from "../workflowData/models";
import type { ClientBrainProposal } from "../clientBrainActions/models";
import type { ChoiceEditorConfig, WorkflowChoiceOption } from "./choiceModel";
import type { IbicEditorConfig } from "./ibicModel";
import type { BoxTaskSettings } from "../../components/NoteWorkflows/boxTaskTypes";

export type DocumentationDraftStatus =
    | "created"
    | "processing"
    | "ready_for_review"
    | "processing_failed"
    | "blocked_no_definition"
    | "finalizing"
    | "finalization_failed"
    | "pending_signature"
    | "approved"
    | "cancelled";

export type DocumentationFieldType =
    | "text"
    | "string"
    | "integer"
    | "number"
    | "boolean"
    | "date"
    | "datetime"
    | "enum"
    | "string_list"
    | "goal_reference"
    | "client_context_reference";

export type DocumentationSourcePolicy =
    | "employee_input"
    | "ai_from_employee_input"
    | "ai_with_client_context"
    | "client_context"
    | "system"
    | "employee_confirmation";

export type LocalizedValue = Record<string, string>;

export type DocumentationFieldDefinition = {
    field_key: string;
    label: LocalizedValue;
    description: string;
    type: DocumentationFieldType;
    required: boolean;
    nullable: boolean;
    source_policy: DocumentationSourcePolicy;
    employee_editable: boolean;
    render_when_empty?: boolean;
    constraints: {
        min_length?: number | null;
        max_length?: number | null;
        min_value?: number | null;
        max_value?: number | null;
        enum_values: string[];
    };
};

export type DocumentationDefinitionStatus = "draft" | "published" | "archived";

export type DocumentationDefinitionVersion = {
    id: string;
    type: "documentation_definition_version";
    schema_version: 1;
    organization_id: string;
    scope_type: "Organization" | "Business Area";
    scope_id: string;
    definition_key: string;
    version: number;
    revision: number;
    status: DocumentationDefinitionStatus;
    display_name: LocalizedValue;
    description: LocalizedValue;
    classification: {
        instructions: string;
        positive_examples: string[];
        negative_examples: string[];
    };
    behaviors: {
        is_default_type: boolean;
        requires_active_goal: boolean;
        creates_deviation_case: boolean;
        requires_ibic: boolean;
        /** Legacy persisted field. Runtime Workflow routing ignores it. */
        documentation_workflow_id?: string | null;
        follow_up_workflow_id?: string | null;
    };
    context_selectors: string[];
    fields: DocumentationFieldDefinition[];
    ai_steps: Array<{
        step_key: string;
        type: "retrieve_context" | "extract_fields" | "generate_fields";
        instructions: string;
        input_fields: string[];
        context_selectors: string[];
        output_fields: string[];
    }>;
    validation_rules: Array<Record<string, unknown>>;
    render_template: {
        title: LocalizedValue;
        sections: Array<{ section_key: string; label: LocalizedValue; field_keys: string[] }>;
    };
    prompt_version: string;
    source_version_id?: string | null;
    test_succeeded_at?: string | null;
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
    published_at?: string | null;
    published_by?: string | null;
    archived_at?: string | null;
    archived_by?: string | null;
};

export type DocumentationDefinitionCreateInput = Pick<
    DocumentationDefinitionVersion,
    | "scope_type"
    | "scope_id"
    | "definition_key"
    | "display_name"
    | "description"
    | "classification"
    | "behaviors"
    | "context_selectors"
    | "fields"
    | "ai_steps"
    | "validation_rules"
    | "render_template"
>;

export type DocumentationDefinitionSnapshot = {
    id: string;
    definition_key: string;
    display_name: LocalizedValue;
    description: LocalizedValue;
    fields: DocumentationFieldDefinition[];
};

export type DocumentationCandidate = {
    definition_version_id: string;
    definition_key: string;
    display_name: LocalizedValue;
    description: LocalizedValue;
};

/** The classifier may also conclude that no candidate Workflow fits the input. */
export type DocumentationClassificationDecision = "matched" | "no_match";

export type DocumentationWorkflowRelevance = {
    workflow_id: string;
    definition_version_id?: string | null;
    applicability: "applicable" | "uncertain" | "not_applicable";
    relevance_score: number;
    reason: string;
    evidence_spans?: string[];
    matched_node_ids: string[];
};

export type DocumentationWorkflowInstance = {
    card_version?: number;
    definition?: DocumentationDefinitionSnapshot;
    id: string;
    event_id: string;
    occurrence_key: string;
    workflow_id: string;
    workflow_title: string;
    definition_version_id: string;
    selected_by: "classifier" | "employee_edit" | "employee_chat";
    selection_reason: string;
    relevance_score: number;
    evidence_spans: string[];
    matched_node_ids: string[];
    status: "ready_for_review" | "blocked" | "failed";
    workflow_snapshot: Record<string, unknown>;
    context_summary?: { profile_version?: number | null; references: Record<string, unknown>[] };
    context_options?: {
        active_goals: Array<{ goal_id: string; title: string; description?: string; version: number }>;
        signed_notes: DocumentationSignedNoteOption[];
    };
    fields: Record<string, DocumentationFieldValue>;
    workflow_node_results: DocumentationWorkflowNodeResult[];
    validation?: { valid: boolean; validated_revision: number; issues: DocumentationValidationIssue[] } | null;
    error?: { code?: string; retryable?: boolean; failed_at?: string } | null;
};

export type DocumentationWorkflowNodeStatus = "applicable" | "insufficient_information";
export type DocumentationWorkflowNodeDisplayMode = "content" | "source_links";

type DocumentationWorkflowNodeResultBase = {
    statistics?: WorkflowStatisticsResult | null;
    client_brain_proposal?: ClientBrainProposal | null;
    ibic_editor?: IbicEditorConfig | null;
    choice_editor?: ChoiceEditorConfig | null;
    output_type?: "text" | "select";
    options?: WorkflowChoiceOption[];
    selected_value?: string | null;
    node_id: string;
    title: string;
    description: string;
    display_mode?: DocumentationWorkflowNodeDisplayMode;
    content: string;
    status: DocumentationWorkflowNodeStatus;
    evidence_spans: string[];
    source_references: Record<string, unknown>[];
    needs_confirmation: boolean;
    confirmed: boolean;
    source: "ai" | "employee_edit" | "system";
    last_modified_by: string;
    last_modified_at: string;
};

export type DocumentationStepNodeResult = DocumentationWorkflowNodeResultBase & {
    node_type: "step";
    parent_subflow_id?: null;
};

export type DocumentationSubflowNodeResult = DocumentationWorkflowNodeResultBase & {
    node_type: "subflow";
    parent_subflow_id?: null;
};

export type DocumentationSubflowStepNodeResult = DocumentationWorkflowNodeResultBase & {
    node_type: "subflow_step";
    parent_subflow_id: string;
};

export type DocumentationOutcomeNodeResult = DocumentationWorkflowNodeResultBase & {
    node_type: "outcome";
    parent_subflow_id?: null;
};

export type DocumentationWorkflowNodeResult =
    | DocumentationStepNodeResult
    | DocumentationSubflowNodeResult
    | DocumentationSubflowStepNodeResult
    | DocumentationOutcomeNodeResult;

export type DocumentationSignedNoteOption = {
    note_id: string;
    title: string;
    timestamp?: number | null;
};

export type DocumentationWorkflowNodeChanges = Record<string, string>;

export type DocumentationFieldValue = {
    value: unknown;
    source: string;
    evidence?: string | null;
    source_reference?: Record<string, unknown> | null;
    confirmed: boolean;
};

export type DocumentationValidationIssue = {
    rule_key: string;
    field_key?: string | null;
    severity: "error" | "warning";
    code: string;
    message: string;
};

export type DocumentationDraft = {
    submitted_card_ids?: string[];
    box_tasks?: Record<string, { instance_id: string; settings: BoxTaskSettings }>;
    box_permission_version?: 1 | 2;
    id: string;
    documentation_group_id: string;
    organization_id: string;
    project_id: string;
    status: DocumentationDraftStatus;
    revision: number;
    created_at?: string;
    versioned_follow_up_count?: number;
    language: string;
    input: { source: "text" | "voice_transcript"; raw_text: string; follow_ups?: string[] };
    conversation_turns?: Array<{
        role: "user" | "assistant";
        content: string;
        kind: "revision" | "workflow_change_request" | "workflow_change_clarification";
        created_at: string;
    }>;
    classification?: {
        decision: DocumentationClassificationDecision;
        /** Absent on a "no_match" decision; the ranking still scores every candidate. */
        workflow_id?: string | null;
        definition_version_id: string;
        relevance_score: number;
        confidence: "high" | "medium" | "low";
        reason: string;
        evidence_spans: string[];
        matched_node_ids: string[];
        matches?: DocumentationWorkflowRelevance[];
        ranking: DocumentationWorkflowRelevance[];
        risk_signals: Array<{ code: string; severity: string; reason: string; evidence?: string | null }>;
    } | null;
    selected_definition?: { definition_version_id: string; definition_key: string } | null;
    selected_workflow?: {
        workflow_id: string;
        title: string;
        selected_by: "classifier" | "employee_chat" | "employee_edit";
    } | null;
    candidate_definitions: DocumentationCandidate[];
    definition?: DocumentationDefinitionSnapshot | null;
    workflow?: {
        id: string;
        title: string;
        description: string;
        steps: Array<{
            id: string;
            title?: string;
            description: string;
            display_mode?: DocumentationWorkflowNodeDisplayMode;
        }>;
        outcomes: Array<{
            id: string;
            title?: string;
            description: string;
            display_mode?: DocumentationWorkflowNodeDisplayMode;
        }>;
        subflows: Array<{
            id: string;
            title?: string;
            description: string;
            display_mode?: DocumentationWorkflowNodeDisplayMode;
            steps: Array<{
                id: string;
                title?: string;
                description: string;
                display_mode?: DocumentationWorkflowNodeDisplayMode;
            }>;
        }>;
    } | null;
    workflow_options?: Array<{ id: string; title: string; description: string }>;
    workflow_node_results: DocumentationWorkflowNodeResult[];
    event_id?: string | null;
    workflow_instances?: DocumentationWorkflowInstance[];
    card_review_enabled?: boolean;
    saved_card_ids?: string[];
    pending_card_id?: string | null;
    changed_saved_card_ids?: string[];
    fields: Record<string, DocumentationFieldValue>;
    validation?: { valid: boolean; validated_revision: number; issues: DocumentationValidationIssue[] } | null;
    context_options?: {
        active_goals: Array<{ goal_id: string; title: string; description?: string; version: number }>;
        signed_notes: DocumentationSignedNoteOption[];
    };
    processing?: { error_code?: string; retryable?: boolean } | null;
    finalization?: { stage?: string; error_code?: string; retryable?: boolean; signed_at?: number } | null;
    approved_note_id?: string | null;
    note_edit?: {
        requires_review?: boolean;
        source_note_id: string;
        source_note_version: number;
        source_draft_id: string;
        mode: "update_unsigned" | "correct_signed";
    } | null;
    review_card_ids?: string[] | null;
};

export type DocumentationGroup = {
    id: string;
    organization_id: string;
    project_ids: string[];
    draft_ids: string[];
    status: string;
    revision: number;
    summary: {
        total: number;
        created: number;
        processing: number;
        ready: number;
        approved: number;
        failed: number;
        cancelled: number;
    };
};

export type DocumentationGroupResponse = {
    group: DocumentationGroup;
    drafts: DocumentationDraft[];
};

export type DocumentationProgressEvent = {
    event: string;
    draft_id?: string;
    revision?: number;
    decision?: DocumentationClassificationDecision;
    confidence?: string;
    reason?: string;
    code?: string;
    retryable?: boolean;
    draft?: DocumentationDraft;
};

export class DocumentationApiError extends Error {
    readonly code: string;
    readonly status: number;
    readonly details?: Record<string, unknown>;

    constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
        super(message);
        this.name = "DocumentationApiError";
        this.code = code;
        this.status = status;
        this.details = details;
    }
}
